import { Flex, Form, Row, Spin, notification } from 'antd';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';

import {
  Entities_Login_StateInfo_Response,
  LoginId,
} from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';
import { Button, Modal, Select } from '@suntex.fim/ui';
import { Send2 } from '@suntex.fim/ui/icons';

import { MAX_MESSAGE_LENGTH } from '../../constants';
import { useCertificateDownload } from '../../hooks/useCertificateDownload';
import { transformMessageContent } from '../../transform-message';
import { DeleteMessageModal } from '../DeleteMessageModal/DeleteMessageModal';
import { RichText } from '../SendMessage/RichText';

type MessageContentProps = {
  message: string | undefined;
  subject?: string;
  senderDisplayName: string;
  unread: boolean;
  modified: Date | undefined;
  notificationId: number;
  /** Called after a message is marked read so the parent can update local state. */
  onMarkedAsRead: (notificationId: string | number) => void;
  user: Entities_Login_StateInfo_Response | undefined;
  senderLoginId: LoginId;
  deleteMessage: (
    notificationId: number | number[],
    loginId: LoginId,
  ) => Promise<void>;
  mode: string;
  receiverDisplayName: string;
  loginId: LoginId;
  isAdminRole: boolean;
  canDelete?: boolean;
};

/** Displays a full message with reply and delete actions. */
export function MessageContent({
  message,
  subject,
  senderDisplayName,
  unread,
  notificationId,
  modified,
  onMarkedAsRead,
  user,
  senderLoginId,
  deleteMessage,
  mode,
  receiverDisplayName,
  loginId,
  isAdminRole = false,
  canDelete = !isAdminRole,
}: MessageContentProps) {
  const { api } = useFramework();
  const { handleClick: handleCertificateClick } = useCertificateDownload();
  const [replyModal, setReplyModal] = useState(false);
  const [content, setContent] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const markAsRead = async () => {
    await api.post_Notification_MarkRead({ notificationId });
    onMarkedAsRead(notificationId);
  };

  useEffect(() => {
    if (unread && notificationId && mode === 'inbox') {
      void markAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId]);

  const replyMessage = async () => {
    try {
      setIsSending(true);
      await api.post_Notification_InsertDirect({
        entityId: user!.entityId,
        receiverLoginId: senderLoginId,
        senderLoginId: user!.id,
        message: content,
        type: 'custom',
        subject,
        hash: undefined,
        receiverName: undefined,
      });
      setContent('');
      setReplyModal(false);
      notification.success({
        message: undefined,
        duration: 5,
        key: 'reply-success',
        description: (
          <Flex align="center" className="toast-message__content">
            <span className="text-chalkboard-txt">
              Reply sent successfully.
            </span>
          </Flex>
        ),
        placement: 'top',
        closeIcon: null,
        className: 'toast-message',
      });
    } catch {
      notification.error({
        message: undefined,
        duration: 5,
        key: 'reply-error',
        description: (
          <Flex align="center" className="toast-message__content">
            <span className="text-chalkboard-txt">
              Message could not be sent
            </span>
          </Flex>
        ),
        placement: 'top',
        closeIcon: null,
        className: 'toast-message',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = () => {
    void deleteMessage(notificationId, loginId);
    setIsDeleteOpen(false);
  };

  if (isSending) return <Spin fullscreen />;

  return (
    <Flex className="open-message text-chalkboard open-message-mobile" vertical>
      {message && message.length > 0 ? (
        <>
          <Flex justify="space-between" align="center" className="subject">
            <h4 className="subject__label fw-bold message-name-mobile">
              {senderDisplayName}
            </h4>
            <Flex
              gap="small"
              className="subject__date-time text-chalkboard-txt message-name-mobile"
            >
              {modified ? format(new Date(modified), 'PP h:mm a') : null}
            </Flex>
          </Flex>
          <Flex justify="space-between" align="center" className="compose">
            <Flex gap={10} vertical className="compose__details">
              <Flex gap="small" className="recipient">
                <span className="text-chalkboard-txt message-name-mobile">
                  To:
                </span>
                <div className="sender fw-bold message-name-mobile">
                  {receiverDisplayName || user?.displayName}
                </div>
              </Flex>
            </Flex>
            <Flex gap="middle" className="compose__action">
              {!isAdminRole && mode === 'inbox' && (
                <Button
                  shape="round"
                  intent="tertiary"
                  size="small"
                  onClick={() => setReplyModal(true)}
                >
                  Reply
                </Button>
              )}

              {/* Reply Modal */}
              <Modal
                closable={false}
                centered
                title="Reply to Message"
                open={replyModal}
                onOk={() => setReplyModal(false)}
                onCancel={() => setReplyModal(false)}
                width={900}
                className="email"
                footer={[
                  <>
                    <Button
                      shape="round"
                      intent="secondary"
                      key="back"
                      onClick={() => setReplyModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      shape="round"
                      intent="primary"
                      key="submit"
                      onClick={() => {
                        setReplyModal(false);
                        void replyMessage();
                      }}
                      icon={<Send2 />}
                      iconPosition="end"
                      disabled={
                        !characterCount || characterCount > MAX_MESSAGE_LENGTH
                      }
                    >
                      Send Message
                    </Button>
                  </>,
                ]}
              >
                <div className="section-to">
                  <Form>
                    <Form.Item colon={false} label="To" name="to">
                      <Select.Tags
                        defaultValue={[senderDisplayName]}
                        tokenSeparators={[',']}
                        className="send-msg"
                        popupClassName="send-msg"
                      />
                    </Form.Item>
                  </Form>
                </div>
                <div className="section-to">
                  <Form.Item colon={false} label="Subject" name="subject">
                    {subject}
                  </Form.Item>
                </div>
                <Row className="section-email richtext-wrapper">
                  <RichText
                    initialValue={content}
                    onChange={(updated, meta) => {
                      setContent(updated);
                      setCharacterCount(meta.characterCount);
                    }}
                  />
                </Row>
              </Modal>

              {canDelete && (
                <Button
                  shape="round"
                  intent="danger"
                  size="small"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Delete
                </Button>
              )}
              <DeleteMessageModal
                isOpen={isDeleteOpen}
                title="Delete Message"
                onOk={handleDelete}
                onCancel={() => setIsDeleteOpen(false)}
                content={
                  <>
                    <p className="fw-bold">
                      Are you sure you want delete this message?
                    </p>
                    <p>
                      Once you delete the message it will be completely removed
                      from the platform.
                    </p>
                  </>
                }
              />
            </Flex>
          </Flex>
          <hr />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- certificate links are <a> tags with href="#"; the delegated handler intercepts them */}
          <div className="email" onClick={handleCertificateClick}>
            {parse(transformMessageContent(message))}
          </div>
        </>
      ) : (
        <h1>FeedBack</h1>
      )}
    </Flex>
  );
}
